import { Check, Prohibit } from "@phosphor-icons/react"
import { type Node } from "@react-types/shared"
import { Box, Row, type MergeWithBoxProps } from "@tokenterminal/ui/Box"
import { Icon } from "@tokenterminal/ui/Icon/Icon"
import { withNoLinesCss } from "@tokenterminal/ui/Text/Text.css"
import {
  type ForwardedRef,
  useContext,
  useEffect,
  type ReactNode,
  Children,
} from "react"
import { mergeProps, useHover, useObjectRef, useOption } from "react-aria"
import {
  ListStateContext,
  UNSTABLE_createLeafComponent as createLeafComponent,
  type ListBoxItemProps,
} from "react-aria-components"
import { Text } from "../../primitives/text/Text"
import { VirtualListContext } from "../select/context/virtual-list-context"
import { iconCss, listItemCss, iconContainerCss } from "./Option.css"

export type ComboboxOptionProps<T> = MergeWithBoxProps<
  {
    children: React.ReactNode
  } & ListBoxItemProps<T>
>

function wrapChildrenWithText(children: ReactNode) {
  return Children.map(children, (child, index) => {
    if (typeof child === "string") {
      return (
        <Text
          size="14"
          color="inherit"
          width="100%"
          key={index}
          style={{
            fontWeight: 400,
          }}
        >
          <Box className={withNoLinesCss} as="span">
            {child}
          </Box>
        </Text>
      )
    }

    return child
  })
}

function ComboboxOptionInner<T>({
  item,
  forwardedRef,
  props,
}: {
  item: Node<T>
  forwardedRef: ForwardedRef<HTMLElement>
  props: MergeWithBoxProps<ListBoxItemProps<object>>
}) {
  const state = useContext(ListStateContext)!
  const ref = useObjectRef(forwardedRef)
  const {
    optionProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    descriptionProps,
    ...states
  } = useOption(item, state, ref)

  const { hoverProps, isHovered } = useHover({
    isDisabled: !states.allowsSelection && !states.hasAction,
    onHoverStart: item.props.onHoverStart,
    onHoverChange: item.props.onHoverChange,
    onHoverEnd: item.props.onHoverEnd,
  })

  useEffect(() => {
    if (!item.textValue) {
      // eslint-disable-next-line no-console
      console.warn(
        "A `textValue` prop is required for <ListBoxItem> elements with non-plain text children in order to support accessibility features such as type to select."
      )
    }
  }, [item.textValue])

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Row
      {...mergeProps(optionProps, hoverProps, props, {
        className: listItemCss,
      })}
      ref={ref}
      data-selected={states.isSelected || undefined}
      data-disabled={
        states.isDisabled || state.disabledKeys.has(item.key) || undefined
      }
      data-hovered={isHovered || undefined}
      data-focused={states.isFocused || undefined}
      data-focus-visible={states.isFocusVisible || undefined}
      data-pressed={states.isPressed || undefined}
    >
      <Row start>{wrapChildrenWithText(item.rendered)}</Row>
      <Box as="span" className={iconContainerCss} fixWhiteSpace>
        <Icon
          width="16"
          height="16"
          as={Check}
          className={iconCss}
          data-selected={true}
        />
        <Icon
          width="16"
          height="16"
          as={Prohibit}
          className={iconCss}
          data-disabled={true}
        />
      </Box>
    </Row>
  )
}

export const ComboboxOption = createLeafComponent(
  "item",
  function (
    props: MergeWithBoxProps<ListBoxItemProps<object>>,
    forwardedRef: ForwardedRef<HTMLElement> | null,
    item: Node<object>
  ) {
    const virtualContext = useContext(VirtualListContext)

    if (virtualContext?.rowVirtualizer) {
      const visibleItems = virtualContext.rowVirtualizer?.getVirtualItems()

      if (!visibleItems || visibleItems.length === 0) {
        return <></>
      }

      const startIndex = visibleItems[0]!.index!
      const endIndex = visibleItems[visibleItems.length - 1]!.index!
      if (item.index! >= startIndex && item.index! <= endIndex) {
        const visibileItem = visibleItems.find(
          (visItem) => visItem.index === item.index
        )

        if (!visibileItem) {
          return <></>
        }

        return (
          <ComboboxOptionInner
            item={item}
            forwardedRef={forwardedRef}
            props={{
              ...props,
              style: {
                ...props.style,
                top: visibileItem.start - virtualContext.offset,
                left: 0,
                height: visibileItem.end - visibileItem.start,
                position: "absolute",
              },
            }}
          />
        )
      }

      return <></>
    } else {
      return (
        <ComboboxOptionInner
          item={item}
          forwardedRef={forwardedRef}
          props={props}
        />
      )
    }
  }
)
